/**
 * Rustige voortgangsregel voor "AI voorbereiden" (tool-feedback #63). Eén regel tekst:
 * "Bronnen ✓ 9 · Callsheet … · Briefing crew · nog ± 30 s  Stoppen". Na afloop een korte
 * samenvatting van wat is ingevuld (per blok ongedaan te maken) en, als er voorstellen
 * liggen bij velden die al gevuld waren, de knop "Voorstel bekijken".
 */
import React from 'react';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { resterendTekst, voortgang } from './aiVoorbereiden';

const GRIJS = '#64748b';
const TEKEN = { klaar: '✓', fout: '!', gestopt: '–', overgeslagen: '–', bezig: '…', wachten: '' };
const knopSx = { textTransform: 'none', fontWeight: 600, minWidth: 0, px: 1, py: 0, fontSize: 13 };

function Stap({ item }) {
  const actief = item.status === 'bezig';
  const klaar = item.status === 'klaar';
  return (
    <Tooltip title={item.titel || ''} disableHoverListener={!item.titel}>
      <Box component="span" data-testid={`ai-stap-${item.key}`} data-status={item.status}
        sx={{ color: actief ? '#0f172a' : klaar ? '#334155' : GRIJS, fontWeight: actief ? 600 : 400, whiteSpace: 'nowrap' }}>
        {item.label}
        {TEKEN[item.status] ? ` ${TEKEN[item.status]}` : ''}
        {item.detail && klaar ? <Box component="span" sx={{ color: GRIJS }}>{` ${item.detail}`}</Box> : null}
        {item.detail && actief ? <Box component="span" sx={{ color: GRIJS }}>{` ${item.detail}`}</Box> : null}
      </Box>
    </Tooltip>
  );
}

export default function AiVoortgang({ job, ingevuld = [], voorstelAantal = 0, punten = 0, fouten = [], onStop, onVoorstel, onHerstel, onSluit, compact = false }) {
  if (!job) return null;
  const bezig = job.status === 'bezig';
  const items = voortgang(job.stappen || []);
  const rest = bezig ? resterendTekst(job.resterendSec) : '';
  const open = ingevuld.filter((x) => !x.hersteld);

  return (
    <Box data-testid="ai-voortgang" role="status" aria-live="polite"
      sx={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap' }}>
        {bezig ? <CircularProgress size={12} thickness={5} sx={{ color: GRIJS }} /> : null}
        <Box component="span" sx={{ fontWeight: 600 }}>
          {bezig && (job.stopGevraagd ? 'AI stopt…' : 'AI bereidt voor')}
          {job.status === 'klaar' && 'AI klaar'}
          {job.status === 'gestopt' && 'Gestopt'}
          {job.status === 'fout' && 'AI voorbereiden mislukt'}
        </Box>
        {items.map((it) => (
          <React.Fragment key={it.key}>
            <Box component="span" sx={{ color: '#cbd5e1' }}>·</Box>
            <Stap item={it} />
          </React.Fragment>
        ))}
        {rest ? <Box component="span" sx={{ color: GRIJS }}>· {rest}</Box> : null}
        {bezig && !job.stopGevraagd && onStop ? (
          <Button size="small" onClick={onStop} sx={{ ...knopSx, color: GRIJS }} data-testid="ai-stoppen">Stoppen</Button>
        ) : null}
        {!bezig && onSluit ? (
          <IconButton size="small" aria-label="Sluiten" onClick={onSluit} sx={{ ml: 'auto', color: GRIJS, p: 0.25 }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}
      </Box>

      {bezig && !compact && (
        <Typography variant="caption" sx={{ color: GRIJS }}>
          Werk gewoon door: lege velden worden ingevuld zodra ze klaar zijn, wat jij al hebt ingevuld blijft staan.
        </Typography>
      )}
      {job.status === 'fout' && job.fout && (
        <Typography variant="caption" sx={{ color: '#b91c1c' }}>{job.fout}</Typography>
      )}
      {job.resultaat?.melding && !bezig && (
        <Typography variant="caption" sx={{ color: GRIJS }}>{job.resultaat.melding}</Typography>
      )}

      {(open.length > 0 || voorstelAantal > 0 || punten > 0) && (
        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 0.75, rowGap: 0.25, flexWrap: 'wrap', color: GRIJS }} data-testid="ai-samenvatting">
          {open.length > 0 && <Box component="span">Ingevuld:</Box>}
          {open.map((x, i) => (
            <Box component="span" key={x.blok} sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <Box component="span" sx={{ color: '#334155' }}>{x.label}</Box>
              <Tooltip title={`${x.label} terugzetten`}>
                <Button size="small" onClick={() => onHerstel?.(x)} sx={{ ...knopSx, color: GRIJS, px: 0.5 }} aria-label={`${x.label} ongedaan maken`}>↶</Button>
              </Tooltip>
              {i < open.length - 1 ? ',' : ''}
            </Box>
          ))}
          {voorstelAantal > 0 && (
            <>
              {open.length > 0 && <Box component="span">·</Box>}
              <Box component="span">{voorstelAantal === 1 ? '1 voorstel bij een veld dat al gevuld was' : `${voorstelAantal} voorstellen bij velden die al gevuld waren`}</Box>
            </>
          )}
          {punten > 0 && (
            <>
              {(open.length > 0 || voorstelAantal > 0) && <Box component="span">·</Box>}
              <Box component="span">{punten === 1 ? '1 punt om na te lopen' : `${punten} punten om na te lopen`}</Box>
            </>
          )}
          {(voorstelAantal > 0 || punten > 0) && onVoorstel && (
            <Button size="small" onClick={onVoorstel} sx={{ ...knopSx, color: '#1e3a8a' }} data-testid="ai-voorstel-bekijken">Voorstel bekijken</Button>
          )}
        </Box>
      )}
      {fouten.length > 0 && (
        <Typography variant="caption" sx={{ color: '#b45309' }}>Niet ingevuld: {fouten.join(' · ')}</Typography>
      )}
    </Box>
  );
}
